import Box from "@mui/material/Box";
import { useState } from "react";
import { useDispatch } from "react-redux";

import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import GetAppIcon from "@mui/icons-material/GetApp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import SwatchesPicker from "../Shared/SwatchesPicker";
import Feature from "./Feature";
import TotalPolygonArea from "./TotalPolygonArea";
import { floorSorter } from "../AppBar/Floor";
import { actionAppBarFloorUpdated } from "../AppBar";
import _ from "lodash";
import { actionMapDrawModeUpdated, actionMapZoomFeaturesUpdated } from "../Map";
import { actionDrawUpdateGroupFeature } from "./drawRedux";
import { xlsx } from "../Shared/downloader";

const expand_ = (theme) => ({
  transform: "rotate(0deg)",
  marginLeft: "auto",
  transition: theme.transitions.create("transform", {
    duration: theme.transitions.duration.shortest,
  }),
});

const expandOpen_ = (theme) => ({
  transform: "rotate(180deg)",
});

const header_ = (theme) => ({
  padding: "0px",
  "& .MuiIconButton-root": {
    padding: "8px",
    fontSize: "1.125rem",
  },
});

const colorPicker_ = (theme) => ({
  right: "unset !important",
  "& .MuiBackdrop-root": {
    backgroundColor: "transparent",
  },
});

export function FeatureGroups({ children }) {
  const zeroPadding = {
    paddingLeft: "0px",
    paddingRight: "0px",
    paddingTop: "0px",
  };

  return <CardContent style={zeroPadding}>{children}</CardContent>;
}

export default function FeatureGroup({ featureGroup = {} }) {
  const dispatch = useDispatch();

  const display = { display: _.isEmpty(featureGroup) ? "none" : "block" };

  const [layerVisible, setLayerVisible] = useState(true);
  const [cardExpanded, setCardExpanded] = useState(true);

  const handleExpandClick = () => {
    setCardExpanded(!cardExpanded);
  };
  const handleDownloadClick = () => {
    xlsx({ features: featureGroup.features, directPropPath: false });
  };
  const onVisibleClick = () => {
    setLayerVisible(!layerVisible);
    const visible = !layerVisible;
    if (visible) {
      dispatch(
        actionMapDrawModeUpdated({
          mode: "show_feature_group",
          data: featureGroup,
        }),
      );
    } else {
      dispatch(
        actionMapDrawModeUpdated({
          mode: "hide_feature_group",
          data: featureGroup,
        }),
      );
    }
  };

  const [colorPickerVisible, setColorPickerVisible] = useState(false);
  const [targetFeature, setTargetFeature] = useState();
  const onColorPickerClick = (feature) => {
    setColorPickerVisible(true);
    setTargetFeature(feature);
  };
  const handleClose = () => {
    setColorPickerVisible(false);
  };
  const handleChange = (color) => {
    const properties = { ...targetFeature.properties, color: color.hex };
    const updatedFeature = { ...targetFeature, properties };
    dispatch(actionDrawUpdateGroupFeature("update", [updatedFeature]));

    // So the color picker will "check" the selected square.
    targetFeature.properties.color = color.hex;
  };

  const [buildings, nonBuilding] = sortByBuilding(featureGroup.features);

  return (
    <Card variant="outlined" style={display}>
      <CardActions disableSpacing sx={header_}>
        <Tooltip title="Toggle drawings">
          <IconButton onClick={onVisibleClick} size="large">
            {layerVisible ? (
              <CheckBoxIcon fontSize="small" />
            ) : (
              <CheckBoxOutlineBlankIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
        <Typography variant="button">Measurement</Typography>
        <Tooltip title="Download drawings">
          <IconButton
            style={{
              marginLeft: "4px",
              display: cardExpanded ? "inherit" : "none",
            }}
            onClick={handleDownloadClick}
            size="large"
          >
            <GetAppIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <IconButton
          sx={[expand_, cardExpanded && expandOpen_]}
          onClick={handleExpandClick}
          aria-expanded={cardExpanded}
          aria-label="show more"
          size="large"
        >
          <ExpandMoreIcon fontSize="small" />
        </IconButton>
      </CardActions>
      <Collapse in={cardExpanded} timeout="auto" unmountOnExit>
        <Buildings>
          {buildings.map((building) => (
            <Building
              key={building.name}
              building={building}
              onColorPickerClick={onColorPickerClick}
            />
          ))}
        </Buildings>
        <NonBuilding>
          {nonBuilding.map((feature) => (
            <Feature
              key={feature.id}
              feature={feature}
              onColorPickerClick={onColorPickerClick}
            />
          ))}
        </NonBuilding>
      </Collapse>
      <Dialog onClose={handleClose} open={colorPickerVisible} sx={colorPicker_}>
        <SwatchesPicker
          color={targetFeature ? targetFeature.properties.color : "#FFFFFF"}
          onChangeComplete={handleChange}
        />
      </Dialog>
    </Card>
  );
}

function Buildings({ children }) {
  const style = { padding: "0px" };
  return <CardContent style={style}>{children}</CardContent>;
}

function NonBuilding({ children }) {
  const style = {
    padding: "0px",
    borderTop: "1px solid rgba(224, 222, 220, 0.4)",
  };

  return <CardContent style={style}>{children}</CardContent>;
}

const header2_ = (theme) => ({
  padding: "0px",
  borderTop: "1px solid rgba(224, 222, 220, 0.4)",
  color: "rgba(0, 0, 0, 0.55)",
  "& .MuiButton-root": {
    fontSize: "0.7rem",
  },
});

const building_ = (theme) => ({
  width: "100%",
  display: "flex",
  justifyContent: "center",
});

const content_ = (theme) => ({
  padding: "0px",
  "&.MuiCardContent-root:last-child": {
    paddingBottom: "8px",
  },
});

function Building({ building = {}, onColorPickerClick }) {
  let { name, features } = { name: "", features: [], ...building };
  const floors = toFloors(features);

  const dispatch = useDispatch();

  const [expandBuilding, setExpandBuilding] = useState(true);
  const [expandIconVisible, setExpandIconVisible] = useState(false);
  const onExpandClick = () => {
    setExpandBuilding(!expandBuilding);
  };
  const onMouseEnter = () => {
    setExpandIconVisible(true);
  };
  const onMouseLeave = () => {
    setExpandIconVisible(false);
  };
  const onBuildingClick = (features) => {
    dispatch(actionMapZoomFeaturesUpdated({ data: features }));
  };
  const onFloorClick = (floor) => {
    dispatch(
      actionMapZoomFeaturesUpdated({ data: filterByFloor(features, floor) }),
    );
    dispatch(actionAppBarFloorUpdated(floor === "BLDG" ? "" : floor));
  };

  return (
    <>
      <CardActions disableSpacing sx={header2_}>
        <Box
          sx={building_}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          <IconButton
            size="small"
            onClick={onExpandClick}
            style={{ visibility: expandIconVisible ? "visible" : "hidden" }}
          >
            {expandBuilding ? (
              <ExpandLessIcon fontSize="small" />
            ) : (
              <ExpandMoreIcon fontSize="small" />
            )}
          </IconButton>
          <BuildingName
            name={name}
            features={features}
            onBuildingClick={onBuildingClick}
          />
          {floors.map((floor) => (
            <Floor key={floor} floor={floor} onFloorClick={onFloorClick} />
          ))}
        </Box>
      </CardActions>
      <Collapse in={expandBuilding} timeout="auto" unmountOnExit>
        <CardContent sx={content_}>
          {features.map((feature) => (
            <Feature
              key={feature.id}
              feature={feature}
              onColorPickerClick={onColorPickerClick}
            />
          ))}
          {features.length > 1 && <TotalPolygonArea features={features} />}
        </CardContent>
      </Collapse>
    </>
  );
}

function BuildingName({ name, features, onBuildingClick }) {
  const onClick = () => {
    onBuildingClick(features);
  };
  return (
    <Tooltip describeChild title="Building">
      <Button size="small" onClick={onClick} color="primary">
        {name}
      </Button>
    </Tooltip>
  );
}

function Floor({ floor, onFloorClick }) {
  const style = { minWidth: 24 };
  const onClick = () => {
    onFloorClick(floor);
  };
  const title = floor === "BLDG" ? "Building View" : `Floor ${floor}`;
  return (
    <Tooltip title={title}>
      <Button size="small" onClick={onClick} color="primary" style={style}>
        {floor}
      </Button>
    </Tooltip>
  );
}

// utils
function sortByBuilding(features = []) {
  if (!features.length) {
    return [[], []];
  }

  const byBuilding = _.groupBy(
    features,
    (feature) => feature.properties.bldname,
  );
  const nonBuilding = byBuilding["undefined"];
  const sortedBuildings = Object.keys(byBuilding)
    .filter((building) => building !== "undefined")
    .sort();
  const buildings = sortedBuildings.map((building) => ({
    name: building,
    features: byBuilding[building],
  }));

  return [buildings || [], nonBuilding || []];
}

function toFloors(features = []) {
  const floors = features
    .filter((feature) => feature.properties.floor)
    .map((feature) => feature.properties.floor.trim());
  const uniqFloors = [...new Set(floors)];
  uniqFloors.sort(floorSorter);
  return uniqFloors.reverse();
}

function filterByFloor(features = [], floor) {
  return features.filter((feature) => {
    return !!feature.properties.floor && feature.properties.floor === floor;
  });
}
